import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, ArrowRight, ArrowLeft, Loader2, ListChecks, Download, AlertTriangle, DollarSign, Package, FileQuestion } from "lucide-react";

const ExceptionBadge = ({ type }) => {
  const config = {
    "Amount Mismatch": { class: "badge-mismatch", icon: DollarSign },
    "Quantity Review": { class: "badge-review", icon: Package },
    "Missing PO": { class: "badge-missing", icon: FileQuestion }
  };
  
  const { class: badgeClass, icon: Icon } = config[type] || { class: "badge-review", icon: AlertTriangle };
  
  return (
    <span className={`${badgeClass} flex items-center gap-1 w-fit`}>
      <Icon size={12} />
      {type}
    </span>
  );
};

export default function GenerateScreen({ 
  runId, 
  exceptions, 
  onGenerate, 
  onFetchExceptions,
  onExport, 
  onBack,
  loading 
}) {
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!runId) {
      navigate("/");
      return;
    }
    // Auto-generate on first visit, or fetch if already generated
    if (!exceptions) {
      autoGenerate();
    }
  }, [runId]);

  const autoGenerate = async () => {
    // Try fetching first — if none exist, generate automatically
    const existing = await onFetchExceptions();
    if (!existing) {
      setGenerating(true);
      try {
        await onGenerate();
      } finally {
        setGenerating(false);
      }
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await onGenerate();
    } finally {
      setGenerating(false);
    }
  };

  const exceptionList = exceptions?.exceptions || [];
  const exceptionCount = exceptions?.exception_count || 0;

  // Calculate stats
  const stats = {
    total: exceptionCount,
    amountMismatch: exceptionList.filter(e => e.exception_type === "Amount Mismatch").length,
    quantityReview: exceptionList.filter(e => e.exception_type === "Quantity Review").length,
    missingPO: exceptionList.filter(e => e.exception_type === "Missing PO").length
  };

  return (
    <div className="max-w-6xl mx-auto animate-fadeIn">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-semibold tracking-tight text-[#062B55]" data-testid="generate-headline">
          Generate exception list
        </h1>
        <p className="text-slate-600 mt-2">
          Review counts and a quick sample.
          <br />
          <span className="text-slate-500">Export when it looks right.</span>
        </p>
      </div>

      {/* Generate Button (if no exceptions yet) */}
      {!exceptions && (
        <Card className="card-surface mb-8">
          <CardContent className="py-12 text-center">
            <div className="empty-state-icon mx-auto">
              <ListChecks size={28} />
            </div>
            <h3 className="text-lg font-semibold text-[#062B55] mt-4">No exception list yet</h3>
            <p className="text-slate-500 mb-6">Generate to view results</p>
            <Button
              onClick={handleGenerate}
              disabled={generating || loading}
              className="btn-primary flex items-center gap-2 mx-auto"
              data-testid="generate-button"
            >
              {generating ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <ListChecks size={18} />
                  Generate list
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      {exceptions && (
        <>
          <div className="stats-grid">
            <div className="stat-card" data-testid="stat-total">
              <p className="stat-label">Total Exceptions</p>
              <p className="stat-value">{stats.total}</p>
            </div>
            <div className="stat-card" data-testid="stat-amount">
              <p className="stat-label">Amount Mismatches</p>
              <p className="stat-value text-red-600">{stats.amountMismatch}</p>
            </div>
            <div className="stat-card" data-testid="stat-quantity">
              <p className="stat-label">Quantity Reviews</p>
              <p className="stat-value text-blue-600">{stats.quantityReview}</p>
            </div>
            <div className="stat-card" data-testid="stat-missing">
              <p className="stat-label">Missing POs</p>
              <p className="stat-value text-amber-600">{stats.missingPO}</p>
            </div>
          </div>

          {/* Exception List Table */}
          <Card className="card-surface mb-8">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold text-[#062B55]">
                  Exception Preview
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleGenerate}
                  disabled={generating}
                  className="text-slate-500 hover:text-[#062B55]"
                  data-testid="regenerate-button"
                >
                  {generating ? <Loader2 size={16} className="animate-spin" /> : "Regenerate"}
                </Button>
              </div>
              <CardDescription>
                Showing {Math.min(50, exceptionList.length)} of {exceptionCount} exceptions
              </CardDescription>
            </CardHeader>
            <CardContent>
              {exceptionList.length === 0 ? (
                <div className="empty-state py-8">
                  <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check size={32} className="text-emerald-600" />
                  </div>
                  <p className="empty-state-title text-emerald-700">No exceptions found!</p>
                  <p className="empty-state-text">All records match across PO, GRN, and Invoice files.</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <Table className="exception-table" data-testid="exception-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[120px]">PO Number</TableHead>
                        <TableHead className="w-[150px]">Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right w-[100px]">PO Amt</TableHead>
                        <TableHead className="text-right w-[100px]">Inv Amt</TableHead>
                        <TableHead className="text-right w-[80px]">GRN Qty</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {exceptionList.slice(0, 50).map((exc, idx) => (
                        <TableRow key={exc.exception_id || idx} data-testid={`exception-row-${idx}`}>
                          <TableCell className="font-mono text-sm">{exc.po_number}</TableCell>
                          <TableCell>
                            <ExceptionBadge type={exc.exception_type} />
                          </TableCell>
                          <TableCell className="text-sm text-slate-600 max-w-[300px] truncate">
                            {exc.description}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            {exc.po_amount != null ? `$${exc.po_amount.toFixed(2)}` : "-"}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            {exc.invoice_amount != null ? `$${exc.invoice_amount.toFixed(2)}` : "-"}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            {exc.grn_qty != null ? exc.grn_qty.toFixed(2) : "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-6">
        <Button
          variant="ghost"
          onClick={onBack}
          className="flex items-center gap-2 text-slate-600 rounded-xl"
          data-testid="generate-back-button"
        >
          <ArrowLeft size={18} />
          Back
        </Button>
        <Button
          onClick={onExport}
          disabled={!exceptions || loading}
          className="btn-primary flex items-center gap-2"
          data-testid="generate-export-button"
        >
          View Dashboard
          <ArrowRight size={18} />
        </Button>
      </div>
    </div>
  );
}
